import { Box, Button, Flex, Grid, Spinner, Text } from '@chakra-ui/react'
import axios from 'axios'
import html2canvas from 'html2canvas'
import React, { useEffect, useRef, useState } from 'react'
import { FiDownload } from 'react-icons/fi'
import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { API_URL } from '../../api/config'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];

const LineGraphs: React.FC = () => {
  const [lineData, setLineData] = useState([]);
  interface PieData {
    name: string;
    value: number;
  }

  const [pieData, setPieData] = useState<PieData[]>([]);
  const [loadingLine, setLoadingLine] = useState(true);
  const [loadingPie, setLoadingPie] = useState(true);

  const lineChartRef = useRef<HTMLDivElement>(null!);
  const pieChartRef = useRef<HTMLDivElement>(null!);

  // 🔄 Fetch do gráfico de linha
  useEffect(() => {
    const fetchLineData = async () => {
      try {
        const response = await axios.get(`${API_URL}users/checkins-access`, {
          headers: { 'Content-Type': 'application/json' }
        });
        setLineData(response.data);
      } catch (error) {
        console.error('Erro ao buscar dados do gráfico de linha:', error);
      } finally {
        setLoadingLine(false);
      }
    };
    fetchLineData();
  }, []);

  // 🥧 Fetch do gráfico de pizza (contadores)
  useEffect(() => {
    const fetchPieData = async () => {
      try {
        const response = await axios.get(`${API_URL}registration/stats`, {
          headers: { 'Content-Type': 'application/json' }
        });
        const { totalRegistrations, totalCheckins, totalAccesses } = response.data;
        setPieData([
          { name: 'Total de Registros', value: totalRegistrations },
          { name: 'Total de Check-ins', value: totalCheckins },
          { name: 'Total de Acessos', value: totalAccesses },
        ]);
      } catch (error) {
        console.error('Erro ao buscar dados do gráfico de pizza:', error);
      } finally {
        setLoadingPie(false);
      }
    };
    fetchPieData();
  }, []);

  // 📸 Função para exportar o gráfico como imagem PNG
  const handleExportCharts = async () => {
    const exportChart = async (ref: React.RefObject<HTMLDivElement>, fileName: string) => {
      if (ref.current) {
        const canvas = await html2canvas(ref.current);
        const link = document.createElement('a');
        link.download = `${fileName}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    };
    await exportChart(lineChartRef, 'grafico_linha');
    await exportChart(pieChartRef, 'grafico_pizza');
  };

  return (
    <>
      <Flex justify="flex-end" mb={4}>
        <Button leftIcon={<FiDownload />} colorScheme="teal" onClick={handleExportCharts}>
          Exportar Gráficos
        </Button>
      </Flex>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={10}>
        {/* 📈 Gráfico de Linha */}
        <Box
          ref={lineChartRef}
          bg="white"
          _dark={{ bg: 'gray.800' }}
          p={6}
          rounded="xl"
          shadow="md"
          minHeight={{ base: '300px', md: '350px' }}
        >
          <Text fontSize={{ base: 'md', md: 'lg' }} mb={4} fontWeight="bold">
            Check-ins e Acessos por Hora
          </Text>
          {loadingLine ? (
            <Flex justify="center" align="center" height="100%">
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={lineData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="top" height={36} />
                <Line type="monotone" dataKey="checkins" stroke="#00C49F" strokeWidth={3} dot={{ r: 5 }} name="Check-ins" />
                <Line type="monotone" dataKey="accesses" stroke="#0088FE" strokeWidth={3} dot={{ r: 5 }} name="Acessos" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* 🥧 Gráfico de Pizza */}
        <Box
          ref={pieChartRef}
          bg="white"
          _dark={{ bg: 'gray.800' }}
          p={6}
          rounded="xl"
          shadow="md"
          height={{ base: '350px', md: '400px' }}
        >
          <Text fontSize={{ base: 'md', md: 'lg' }} mb={4} fontWeight="bold">
            Distribuição dos Contadores
          </Text>
          {loadingPie ? (
            <Flex justify="center" align="center" height="100%">
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  outerRadius={90}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Grid>
    </>
  );
};

export default LineGraphs;
